import { api, ApiError } from '@/api/client';
import { isOnline, onOnline } from '@/native';
import * as db from './db';
import { randomId, sha256Hex } from '@/lib/checksum';
import {
  EMPTY_SNAPSHOT,
  type QueueItem,
  type QueueKind,
  type QueueSnapshot,
  type QueuedFile,
  type QueuedFileKind,
} from './types';

/**
 * 上传队列。整个工人端的命脉。
 *
 * 流程：
 *   入队（UI 立刻显示成功）→ 后台 Worker 逐条处理
 *   → 每个文件 presign → PUT 直传 → 业务接口提交 → 删本地文件
 *
 * 一次只处理一条：工地信号弱，并发上传只会互相抢带宽、把成功率拖下去。
 */

/** 退避梯度。连续失败时越等越久，避免在没信号时空转耗电。 */
const BACKOFF_MS = [30_000, 60_000, 5 * 60_000, 15 * 60_000, 60 * 60_000];

/** 重试上限。到了还失败就标 FAILED，交给工人和管理员处理，不再无限重试。 */
const MAX_ATTEMPTS = 10;

/** 空闲轮询间隔。主要靠网络恢复事件驱动，这个只是兜底。 */
const POLL_INTERVAL_MS = 30_000;

type Listener = (snapshot: QueueSnapshot) => void;

let currentUserId: number | null = null;
let running = false;
let timer: number | null = null;
let stopNetworkListener: (() => void) | null = null;
const listeners = new Set<Listener>();
let snapshot: QueueSnapshot = EMPTY_SNAPSHOT;

// ---------------------------------------------------------------- 对外接口

/**
 * 绑定当前登录用户并启动 Worker。
 *
 * 手机多人共用（CR-003），队列必须按用户隔离：A 的待传数据不能在 B 登录后
 * 被当成 B 的数据传上去。切换用户时旧数据留在本地，等 A 再登录才继续传。
 */
export async function startQueue(userId: number): Promise<void> {
  if (currentUserId === userId && running) return;

  currentUserId = userId;
  // 上次进程被杀时卡在 UPLOADING 的，复位成 PENDING 才轮得到
  await db.resetStuck();
  await refreshSnapshot();

  if (running) return;
  running = true;

  stopNetworkListener = onOnline(() => {
    // 网络刚恢复就立刻试，不等下一轮轮询 —— 工人可能只是路过一个有信号的地方
    void tick();
  });

  scheduleNext(0);
}

/** 退出登录时调用。队列数据留在本地，本人下次登录会接着传。 */
export function stopQueue(): void {
  running = false;
  currentUserId = null;
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
  stopNetworkListener?.();
  stopNetworkListener = null;
  setSnapshot(EMPTY_SNAPSHOT);
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(snapshot);
  return () => listeners.delete(listener);
}

export function getSnapshot(): QueueSnapshot {
  return snapshot;
}

export interface EnqueueInput {
  kind: QueueKind;
  userId: number;
  siteId: number;
  payload: Record<string, unknown>;
  files?: Array<{ blob: Blob; mimeType: string }>;
}

/** 队列里所有待传文件的字节数超过这个值就不再收新的，留点余地给退出前的收尾。 */
const QUOTA_HEADROOM_BYTES = 32 * 1024 * 1024;

/**
 * 入队前先问一句存储还够不够。
 *
 * 安卓 WebView 的 IndexedDB 配额通常只有可用磁盘的几个百分点，
 * 而现在一条日报可能带一段 100MB 的视频。配额满了 `put` 会抛
 * QuotaExceededError —— 改之前这条路径完全没处理，工人看到的是
 * 「提交失败」，照片和视频当场丢掉，而他人还在工地上。
 *
 * `navigator.storage.estimate()` 不是每个环境都有，取不到就放行 ——
 * 宁可让 put 去抛，也不能因为探测不到就不让人提交。
 */
async function assertStorageRoom(bytes: number): Promise<void> {
  if (!navigator.storage?.estimate) return;
  try {
    const { quota, usage } = await navigator.storage.estimate();
    if (quota === undefined || usage === undefined) return;
    if (usage + bytes + QUOTA_HEADROOM_BYTES > quota) {
      throw new QueueStorageFullError();
    }
  } catch (error) {
    if (error instanceof QueueStorageFullError) throw error;
    // estimate 本身失败不该挡住提交
  }
}

/** 手机存储不够，装不下这次要传的东西。UI 按 `queue.storageFull` 翻译。 */
export class QueueStorageFullError extends Error {
  constructor() {
    super('storage full');
    this.name = 'QueueStorageFullError';
  }
}

/** 按 mimeType 定附件种类：视频走 VIDEO，采购的是小票，其余是照片。 */
function fileKindOf(itemKind: QueueKind, mimeType: string): QueuedFileKind {
  if (mimeType.startsWith('video/')) return 'VIDEO';
  return itemKind === 'PURCHASE' ? 'RECEIPT' : 'PHOTO';
}

/**
 * 入队。**同步落盘后立刻返回** —— 调用方拿到返回就可以给用户显示成功，
 * 不用等网络。这是「乐观提交」，也是工人体验的关键：
 * 在没信号的地下室拍完照，界面也该立刻确认收到。
 */
export async function enqueue(input: EnqueueInput): Promise<string> {
  const totalBytes = (input.files ?? []).reduce((sum, file) => sum + file.blob.size, 0);
  if (totalBytes > 0) await assertStorageRoom(totalBytes);

  const id = randomId();
  const now = Date.now();

  const item: QueueItem = {
    id,
    userId: input.userId,
    siteId: input.siteId,
    kind: input.kind,
    payload: input.payload,
    status: 'PENDING',
    attempts: 0,
    nextAttemptAt: now,
    createdAt: now,
    updatedAt: now,
  };

  for (const [index, file] of (input.files ?? []).entries()) {
    const queued: QueuedFile = {
      id: `${id}-${String(index).padStart(3, '0')}`,
      itemId: id,
      clientId: randomId(),
      blob: file.blob,
      mimeType: file.mimeType,
      kind: fileKindOf(input.kind, file.mimeType),
      sizeBytes: file.blob.size,
      // 大文件会把主线程卡住几秒（100MB 的视频尤其明显）。这次接受 ——
      // 它发生在「按下提交」那一刻，界面本来就该显示忙碌状态
      sha256: await sha256Hex(file.blob),
    };
    await db.putFile(queued);
  }

  await db.putItem(item);
  await refreshSnapshot();

  // 有网就马上试一次，没网也无所谓，Worker 会兜住
  void tick();
  return id;
}

/** 人工重试那些已经放弃的条目 */
export async function retryFailed(): Promise<void> {
  if (currentUserId === null) return;
  const items = await db.listItems(currentUserId);
  for (const item of items) {
    if (item.status !== 'FAILED') continue;
    await db.putItem({
      ...item,
      status: 'PENDING',
      attempts: 0,
      nextAttemptAt: Date.now(),
      lastError: undefined,
      updatedAt: Date.now(),
    });
  }
  await refreshSnapshot();
  void tick();
}

/** 丢弃一条传不上去的记录。只有工人明确确认才会走到这里。 */
export async function discard(itemId: string): Promise<void> {
  await db.deleteItem(itemId);
  await refreshSnapshot();
}

// ---------------------------------------------------------------- Worker

function scheduleNext(delayMs: number): void {
  if (!running) return;
  if (timer !== null) window.clearTimeout(timer);
  timer = window.setTimeout(() => {
    void tick();
  }, delayMs);
}

let ticking = false;

async function tick(): Promise<void> {
  if (!running || ticking || currentUserId === null) return;
  ticking = true;

  try {
    if (!(await isOnline())) {
      scheduleNext(POLL_INTERVAL_MS);
      return;
    }

    const item = await db.nextPending(currentUserId, Date.now());
    if (!item) {
      await db.purgeDone(currentUserId);
      await refreshSnapshot();
      scheduleNext(POLL_INTERVAL_MS);
      return;
    }

    await processItem(item);
    await refreshSnapshot();

    // 还有活就立刻接着干，别等轮询
    scheduleNext(500);
  } catch (error) {
    console.warn('[queue] tick 异常', error);
    scheduleNext(POLL_INTERVAL_MS);
  } finally {
    ticking = false;
  }
}

async function processItem(item: QueueItem): Promise<void> {
  await db.putItem({ ...item, status: 'UPLOADING', updatedAt: Date.now() });

  try {
    const files = await db.listFiles(item.id);

    // 逐个文件直传。已经有 fileKey 的跳过 —— 上次可能传到一半断了，
    // 没必要重传已经进了存储的文件
    for (const file of files) {
      if (file.fileKey) continue;
      const fileKey = await uploadFile(item, file);
      await db.putFile({ ...file, fileKey });
    }

    const uploaded = await db.listFiles(item.id);
    const result = await submit(item, uploaded);

    // 成功后立刻删本地文件：照片很占空间，工地手机存储通常不宽裕
    await db.deleteItem(item.id);

    // 服务端的判定结果只有这里知道。下班打卡要不要退出登录取决于它 ——
    // 被判超范围的打卡不能退出，否则工人得重新登录才能补打（CR-006）。
    notifyUploaded(item, result);
  } catch (error) {
    await handleFailure(item, error);
  }
}

/** presign → PUT 到对象存储。字节不经过 API 服务器。 */
async function uploadFile(item: QueueItem, file: QueuedFile): Promise<string> {
  const { uploadUrl, fileKey } = await api.post<{ uploadUrl: string; fileKey: string }>(
    '/files/presign',
    {
      siteId: item.siteId,
      // 老条目（CR-023 之前入队的）没有 kind，按旧规则兜底
      kind: file.kind ?? (item.kind === 'PURCHASE' ? 'RECEIPT' : 'PHOTO'),
      mimeType: file.mimeType,
      sizeBytes: file.sizeBytes,
    },
  );

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.mimeType },
    body: file.blob,
  });
  if (!response.ok) {
    throw new Error(`直传失败 HTTP ${response.status}`);
  }

  return fileKey;
}

/**
 * 条目上传成功后的回调。
 *
 * 队列本身不认识业务语义，但「这条打卡服务端判成了什么」只有这一刻知道，
 * 所以把服务端的返回原样交出去，由业务侧决定后续动作。
 *
 * **是一组监听者，不是一个。** 改之前只有一个槽位，谁后注册谁把前一个顶掉 ——
 * 而现在至少有两个主顾：会话层等下班卡传完才退出登录，界面层等打卡传完
 * 刷新班次状态。一个槽位的话，后者会把前者挤掉，工人打完下班卡永远退不出去。
 */
export type UploadedListener = (item: QueueItem, result: unknown) => void;

const uploadedListeners = new Set<UploadedListener>();

/** 返回取消订阅的函数。调用方负责在不用时取消，别把监听者留在集合里。 */
export function addUploadedListener(listener: UploadedListener): () => void {
  uploadedListeners.add(listener);
  return () => uploadedListeners.delete(listener);
}

function notifyUploaded(item: QueueItem, result: unknown): void {
  for (const listener of uploadedListeners) {
    try {
      listener(item, result);
    } catch (error) {
      // 一个回调出错不能影响其他回调，更不能影响队列继续干活
      console.warn('[queue] uploaded 回调异常', error);
    }
  }
}

/**
 * 这条记录是不是真的在队列里等过。
 *
 * 有网时入队到发出去通常在几百毫秒内，超过这个门槛（或者重试过）
 * 才说明当时确实没传出去。门槛给得宽一点：慢网下第一次尝试本来就可能要几秒。
 */
const QUEUED_THRESHOLD_MS = 10_000;

function didWait(item: QueueItem): boolean {
  return item.attempts > 0 || Date.now() - item.createdAt > QUEUED_THRESHOLD_MS;
}

/** 把业务数据提交给对应接口。clientId 用条目 id，服务端据此幂等。 */
async function submit(item: QueueItem, files: QueuedFile[]): Promise<unknown> {
  const fileRefs = files.map((file) => ({
    clientId: file.clientId,
    fileKey: file.fileKey!,
    mimeType: file.mimeType,
    sha256: file.sha256,
  }));

  switch (item.kind) {
    case 'PUNCH':
      return api.post('/punches', {
        ...item.payload,
        clientId: item.id,
        siteId: item.siteId,
        // **只有真的在队列里等过才算离线补传。**
        // 改之前这里写死 true —— 而所有打卡都走队列，于是每条记录都被标成
        // OFFLINE_SYNC，管理端看到的全是「离线补传」；更要紧的是服务端据此
        // 跳过时钟偏差检查，那道防线等于从来没生效过（手机时间调早两小时也照收）。
        offline: didWait(item),
      });

    case 'HIDDEN_WORK':
      return api.post('/hidden-works', {
        ...item.payload,
        clientId: item.id,
        siteId: item.siteId,
        fileKeys: fileRefs,
      });

    case 'DAILY_LOG':
      // **只透传 workDate 和 content**，不 `...item.payload`（CR-023）。
      // 本地 IndexedDB 里可能还躺着改版前入队的条目，带着 taskId / fieldValues /
      // lat / lng —— 服务端的 forbidNonWhitelisted 会 400，而 400 在这个队列里
      // 算永久失败，那条日报就再也传不上去了。
      return api.post('/daily-logs', {
        clientId: item.id,
        siteId: item.siteId,
        workDate: item.payload.workDate,
        content: item.payload.content,
        fileKeys: fileRefs,
      });

    case 'PURCHASE':
      return api.post('/purchases', {
        ...item.payload,
        clientId: item.id,
        siteId: item.siteId,
        fileKeys: fileRefs,
      });
  }
}

async function handleFailure(item: QueueItem, error: unknown): Promise<void> {
  const attempts = item.attempts + 1;
  const code = error instanceof ApiError ? error.code : 'network';

  // 业务校验失败（400/422）重试多少次都一样，直接标失败让人来看。
  // 只有网络类和服务端 5xx 才值得重试。
  const permanent =
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 429;

  const exhausted = attempts >= MAX_ATTEMPTS;
  const backoff = BACKOFF_MS[Math.min(attempts - 1, BACKOFF_MS.length - 1)] ?? 60_000;

  await db.putItem({
    ...item,
    status: permanent || exhausted ? 'FAILED' : 'PENDING',
    attempts,
    nextAttemptAt: Date.now() + backoff,
    lastError: code,
    updatedAt: Date.now(),
  });
}

// ---------------------------------------------------------------- 快照

async function refreshSnapshot(): Promise<void> {
  if (currentUserId === null) {
    setSnapshot(EMPTY_SNAPSHOT);
    return;
  }

  const items = (await db.listItems(currentUserId)).filter((item) => item.status !== 'DONE');
  const pendingFiles = await db.countPendingFiles(currentUserId);

  setSnapshot({
    pending: items.filter((i) => i.status === 'PENDING').length,
    uploading: items.filter((i) => i.status === 'UPLOADING').length,
    failed: items.filter((i) => i.status === 'FAILED').length,
    pendingFiles,
    items,
  });
}

function setSnapshot(next: QueueSnapshot): void {
  snapshot = next;
  for (const listener of listeners) listener(next);
}

// ---------------------------------------------------------------- 工具

/** 算文件的 SHA-256，用于证明原图未被篡改 */
// sha256Hex 在 lib/checksum.ts，和管理端的直传共用一份；http 页面上没有 crypto.subtle 也能算（CR-058）
